import type { UpdateUserInfoRequestDto, JobTitle } from '@/types/api/user';
import type { User } from '@/types/admin';
import { parsePositiveUserId } from '@/utils/userUtils';

/** 사용자 관리 모달의 역할 라벨 → PATCH /user/admin/update-user 의 role */
export function adminUiRoleToApiRole(label: string): number | undefined {
	const n = label.trim();
	if (n === 'Student') return 1;
	if (n === 'Master') return 2;
	if (n === 'Admin') return 5;
	return undefined;
}

/** 한글 직무명 → 백엔드 JobTitle enum 매핑 */
export function koreanJobTitleToApiJobTitle(koreanTitle: string): JobTitle | undefined {
	const trimmed = koreanTitle.trim();
	const mapping: Record<string, JobTitle> = {
		'의사': 'DOCTOR',
		'간호사': 'NURSE',
		'간호조무사': 'NURSING_AIDE',
		'응급구조사': 'PARAMEDIC',
		'교육자': 'EDUCATOR',
		'학생': 'STUDENT',
		'연구원': 'RESEARCHER',
	};
	return mapping[trimmed];
}

export type BuildAdminUpdateUserPayloadResult =
	| { type: 'patch'; payload: UpdateUserInfoRequestDto }
	| { type: 'noop'; reason: 'invalid_id' | 'no_changes' };

/**
 * UserManagement·기관 UserList 등에서 공통으로 사용.
 * PATCH /user/admin/update-user 허용 필드만 담는다.
 */
export function buildAdminUpdateUserPayload(prev: User, updated: User): BuildAdminUpdateUserPayloadResult {
	const userId = parsePositiveUserId(updated.id);
	if (userId === undefined) {
		return { type: 'noop', reason: 'invalid_id' };
	}

	const isGuestUser = prev.accountType === '게스트' || prev.type === 'guest';
	const roleChanged = !isGuestUser && updated.role !== prev.role;

	const payload: UpdateUserInfoRequestDto = { userId };

	if (roleChanged) {
		const roleNum = adminUiRoleToApiRole(updated.role);
		if (roleNum !== undefined) payload.role = roleNum;
	}
	if (updated.status !== prev.status) {
		payload.isActive = updated.status === 'active';
	}
	if ((updated.phoneNumber ?? '') !== (prev.phoneNumber ?? '')) {
		payload.phoneNumber = (updated.phoneNumber ?? '').trim();
	}
	if ((updated.position ?? '') !== (prev.position ?? '')) {
		payload.position = (updated.position ?? '').trim();
	}
	if ((updated.department ?? '') !== (prev.department ?? '')) {
		const departmentValue = (updated.department ?? '').trim();
		if (departmentValue) {
			const apiJobTitle = koreanJobTitleToApiJobTitle(departmentValue);
			if (apiJobTitle) {
				payload.jobTitle = apiJobTitle;
			} else {
				// 매핑되지 않은 경우 직접입력으로 처리
				payload.jobTitle = 'CUSTOM';
				payload.customJobTitle = departmentValue;
			}
		}
	}
	const nextAffiliation = updated.affiliation ?? updated.organization ?? '';
	const prevAffiliation = prev.affiliation ?? prev.organization ?? '';
	if (nextAffiliation !== prevAffiliation) {
		payload.affiliation = nextAffiliation.trim();
	}

	if (isGuestUser) {
		const pw = (updated.password ?? '').trim();
		if (pw) payload.newPassword = pw;
		const nextExp = (updated.expirationDate ?? '').trim();
		const prevExp = (prev.expirationDate ?? '').trim();
		if (nextExp !== prevExp) {
			payload.guestAccountExpiresAt = nextExp.length > 0 ? nextExp : null;
		}
	}

	const { userId: _uid, ...extras } = payload;
	const hasPatchBody = Object.entries(extras).some(([key, v]) => {
		if (v === undefined) return false;
		if (v === null) return true;
		if (typeof v === 'boolean') return true;
		if (typeof v === 'number') return true;
		if (typeof v === 'string') {
			if (key === 'affiliation') return prevAffiliation.trim() !== v.trim();
			if (key === 'phoneNumber') return (prev.phoneNumber ?? '').trim() !== v.trim();
			if (key === 'position') return (prev.position ?? '').trim() !== v.trim();
			if (key === 'customJobTitle') return (prev.department ?? '').trim() !== v.trim();
			return true;
		}
		return true;
	});

	if (!hasPatchBody) {
		return { type: 'noop', reason: 'no_changes' };
	}
	return { type: 'patch', payload };
}
